import { useUserStore } from '../../../store/useUserStore';
import { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';


import {
  useFreePostDetailQuery,
  useFreeCommentsInfiniteListQuery,
  useCreateFreePostCommentMutation,
  useToggleFreePostLikeMutation,
} from '../../../queries';
import { COMPLAINT_TARGET_TYPE } from '../../../data/define';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import IcLike from '../../../assets/image/community/ic_like.svg';
import IcLikeFilled from '../../../assets/image/community/ic_like_filled.svg';
import IcProfile from '../../../assets/image/ic_profile.svg';
import FullHeightContainer from '../../common/FullHeightContainer';
import TopHeaderWithPrev from '../../common/TopHeaderWithPrev';
import Divider from '../../common/Divider';
import CommentCard from '../CommentCard';
import BottomFixedTextField from '../../common/BottomFixedTextField';
import OptionDialog from './OptionDialog';
import ComplaintDialog from '../ComplaintDialog';

const DateSort = {
  Registration: 1,
  Latest: 2,
};

const LIMIT = 10;

interface PostDetailProps {
  drawerOpen: boolean;
  onClickPrev: () => void;
  postId?: number | null;
}

function PostDetail({ drawerOpen, onClickPrev, postId = null }: PostDetailProps) {
  const user = useUserStore();
  const [optionOpenId, setOptionOpenId] = useState(null);
  const [complaintOpenId, setComplaintOpenId] = useState(null);
  const [dateSorting, setDateSorting] = useState(DateSort.Latest);
  const [inputValue, setInputValue] = useState('');

  // 1. 상세조회 Query
  const { data: detail } = useFreePostDetailQuery(user.id || 0, postId || 0, {
    enabled: drawerOpen && !!user?.id && !!postId,
  });

  // 2. 댓글 무한스크롤 Query
  const {
    data: commentsData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchComments,
  } = useFreeCommentsInfiniteListQuery(
    {
      postId: postId || 0,
      limit: LIMIT,
      order: dateSorting === DateSort.Registration ? 'ASC' : 'DESC',
      sortBy: dateSorting === DateSort.Registration ? 'createdAt' : 'updatedAt',
    },
    { enabled: drawerOpen && !!postId }
  );

  const comments = useMemo(
    () => commentsData?.pages.flatMap((page: any) => page.items) || [],
    [commentsData]
  );


  // 3. 댓글 작성 Mutation
  const { mutate: createComment } = useCreateFreePostCommentMutation(postId || 0);

  // 4. 좋아요 토글 Mutation
  const { mutate: toggleLike } = useToggleFreePostLikeMutation(postId || 0, user.id || 0);

  // infinite scroll 연동
  const { lastItemRef } = useInfiniteScroll({
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  const handleCommentSubmit = useCallback(async () => {
    if (!inputValue.trim()) return;
    createComment(
      { content: inputValue },
      {
        onSuccess: () => {
          setInputValue('');
        },
        onError: (error) => {
          console.error('Failed to create comment', error);
          toast.error('댓글 생성에 실패했습니다.');
        },
      }
    );
  }, [inputValue, createComment]);

  const handleLikeToggle = useCallback(() => {
    toggleLike(null, {
      onError: (error) => {
        console.error('Failed to toggle like', error);
        toast.error('좋아요를 등록하는 데 실패했습니다.');
      },
    });
  }, [toggleLike]);

  const handleComplaintReport = (selectedId) => {
    setComplaintOpenId(selectedId);
    setOptionOpenId(null);
  };

  // 옵션버튼 클릭
  const handleOptionClick = useCallback((targetPostId) => {
    setOptionOpenId(targetPostId);
  }, []);

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title="자유게시판" onClickPrev={onClickPrev} />
      <div className="pageInner">
        <div className="font-[700] text-[#1C1C1C] text-[18px] mt-5">{detail?.title}</div>
        <div className="w-full flex justify-start gap-2 items-center border-b border-[#EAEAEA] py-3 mb-6">
          <div className="shrink-0 min-w-[52px] min-h-[52px] bg-[#FFF] border border-[#EAEAEA] overflow-hidden flex items-center justify-center rounded-[24px]">
            <img
              src={detail?.author?.imageUrl ? detail?.author?.imageUrl : IcProfile}
              alt="profile"
              className="w-[52px] object-cover rounded-full"
            />
          </div>
          <div className="gap-1 flex flex-col items-start justify-between font-[400] text-[#363636] text-[12px]">
            <div className="font-[700] text-[#1C1C1C] text-[16px]">
              {detail?.author?.nickName ?? 'UNKNOWN'}
            </div>
            <div className="font-[400] text-[#969696] text-[12px]">
              {detail?.createdAt ? moment(detail.createdAt).format('YY.MM.DD') : ''}
            </div>
          </div>
        </div>
        <div className="w-full text-left text-[14px] text-[#363636] font-[400] whitespace-pre-line">
          <div className="">{detail?.content}</div>
        </div>
        <div className="w-full mt-5">
          <button
            type="button"
            className="px-4 py-2 rounded-[25px] flex justify-center items-center gap-1 border border-[#E0E0E0]"
            onClick={() => handleLikeToggle()}
            style={detail?.isLiked ? { backgroundColor: '#F0FCFC', borderColor: '#5EBEBB' } : {}}
          >
            <img src={detail?.isLiked ? IcLikeFilled : IcLike} alt="like" />
            <span className="text-[14px] text-[#363636]">
              {detail?.likeCount === 0 || !detail ? '좋아요' : `${detail?.likeCount}`}
            </span>
          </button>
        </div>
        <div className="my-[24px] w-full">
          <Divider />
        </div>
        <div className="w-full flex justify-between">
          <div className="flex justify-start items-center text-[16px] font-[700] gap-1">
            댓글{comments.length}
          </div>
          <div className="flex justify-end items-center text-[14px] font-[700] gap-2">
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Registration && comments.length !== 0
                    ? '#363636'
                    : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Registration)}
              disabled={comments.length === 0}
            >
              등록순
            </button>
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Latest && comments.length !== 0 ? '#363636' : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Latest)}
              disabled={comments.length === 0}
            >
              최신순
            </button>
          </div>
        </div>
        <div className="w-full">
          {comments.length >= 1 ? (
            comments.map((comment, idx) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                optionClick={handleOptionClick}
                ref={idx === comments.length - 1 ? lastItemRef : undefined}
              />
            ))
          ) : (
            <div className="text-center text-[#767676] text-[14px] mt-20">
              다른 사용자의 답변이 아직 없습니다.
              <br /> 조금만 기다려주세요.
            </div>
          )}
          <OptionDialog
            open={optionOpenId !== null}
            onClose={() => setOptionOpenId(null)}
            selectedId={optionOpenId}
            isUsersPost={
              comments.find((item) => item.id === optionOpenId)?.author?.id === user?.id
            }
            reload={() => refetchComments()}
            onComplaintClick={handleComplaintReport}
          />
        </div>
        {complaintOpenId !== null && (
          <ComplaintDialog
            open={complaintOpenId}
            onClose={() => setComplaintOpenId(null)}
            targetType={COMPLAINT_TARGET_TYPE.FREEBOARD_COMMENT}
          />
        )}
      </div>
      <BottomFixedTextField
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={handleCommentSubmit}
      />
    </FullHeightContainer>
  );
}

export default PostDetail;
